import { GlobalEnumsAceTypphase, MilestoneDisplayModel, MilestoneDisplayModelFormGroup, PhaseDisplayModel, ScheduleDisplayModelFormGroup } from '@/api/model-index';
import { ConfirmationDialogAction, ConfirmationDialogComponent, ConfirmationDialogResult } from '@/core/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { I18nService } from '@/core/services/i18nService';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { EditEvent, GridDataResult, RemoveEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { MilestoneDialogComponent, MilestoneDialogResult } from '../../../dialogs/milestone-dialog/milestone-dialog.component';
import { Constants } from '@/constants';

@Component({
  selector: 'schedule-data',
  templateUrl: './schedule-data.component.html',
  styleUrls: ['./schedule-data.component.scss']
})
export class ScheduleDataComponent {
  @Output() saveEvent = new EventEmitter();
  
  @Input('form') scheduleDataForm: ScheduleDisplayModelFormGroup;
  @Input('currentPhase') currentPhase: PhaseDisplayModel | null | undefined;
  @Input('currentReportId') currentReportId: string | null |undefined;

  public editedFormGroup: MilestoneDisplayModelFormGroup | undefined;
  protected editedRowIndex: number | undefined;
  protected milestoneDialog: DialogRef;
  public sort: SortDescriptor[] = [
    { field: "datumPlan", dir: "asc" }
  ];

  @Input('grid')
  public gridView: GridDataResult; 

  get milestonesFormArray(): FormArray {
    return this.scheduleDataForm?.Milestones;
  }

  constructor(protected dialogService: DialogService, protected langService: I18nService) {
  }

  protected removeHandler({ rowIndex }: RemoveEvent): void {
    this.openDeleteDialog(rowIndex);
  }

  protected openDeleteDialog(index: number) {
    let deleteDialogRef = this.dialogService.open({
      content: ConfirmationDialogComponent,
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      title: this.langService.instant("schedule-data.dialog.titleDelete")
    });
    let dialogComponent: ConfirmationDialogComponent = deleteDialogRef.content.instance;
    dialogComponent.content = this.langService.instant("schedule-data.dialog.content");
    deleteDialogRef.result.subscribe(result => this.onDeleteDialogClose(<ConfirmationDialogResult>result, index));
  }
  protected onDeleteDialogClose(result: ConfirmationDialogResult, index: number) {
    switch (result.action) {
      case ConfirmationDialogAction.Confirm:
        this.milestonesFormArray.removeAt(index);
        this.milestonesFormArray.markAsDirty();
        this.loadGridData();
        this.saveEvent.emit();
        break;
      case ConfirmationDialogAction.Cancel:
      default:
        break;
    }
  }

  public loadGridData(): void {
    let sorted = this.scheduleDataForm.Milestones.value.sort((valueA: MilestoneDisplayModel, valueB: MilestoneDisplayModel) => Date.parse(valueA.datumPlan??"") - Date.parse(valueB.datumPlan??""));
    this.scheduleDataForm.Milestones.patchValue(sorted);
    this.gridView = {
      data:  sorted,
      total: this.scheduleDataForm.Milestones.value.length,
    };
  }

  public createMilestone() {
    this.openMilestoneDialog(null);
  }

  public editMilestone(args: EditEvent) {
    this.openMilestoneDialog(args);
  }

  public openMilestoneDialog(args: EditEvent | null) {
    if (args != null){
      this.editedRowIndex = args.rowIndex;
    }

    this.milestoneDialog = this.dialogService.open({
      content: MilestoneDialogComponent,
      maxWidth: Constants.DIALOG.MAX_WIDTH,
      maxHeight: Constants.DIALOG.MAX_HEIGHT,
      minWidth: "30%",
      minHeight: "50%",
      title: this.editedRowIndex != null ? this.langService.instant("schedule-data.dialog.titleEdit") : this.langService.instant("schedule-data.dialog.titleCreate") 
    });

    let dialogComponent: MilestoneDialogComponent = this.milestoneDialog.content.instance;
    dialogComponent.milestoneIndex = this.editedRowIndex;
    dialogComponent.currentBerichtId = this.currentReportId;

    if (this.editedRowIndex != undefined) {
      let milestone: MilestoneDisplayModelFormGroup | FormControl<null> = this.scheduleDataForm.Milestones.at(this.editedRowIndex);
      
      if (milestone instanceof MilestoneDisplayModelFormGroup) {
        if (milestone.DatumPlan.value != null)
          milestone.DatumPlan.patchValue(new Date(milestone.DatumPlan.value));

        if (milestone.DatumIst.value != null)
          milestone.DatumIst.patchValue(new Date(milestone.DatumIst.value));
      }

      dialogComponent.milestoneFormGroup.patchValue(milestone.value);
    }
    else {
      let milestone: MilestoneDisplayModelFormGroup = MilestoneDisplayModelFormGroup.Instance;
      
      if (this.currentPhase != null)
      {
        milestone.ErstelltInPhaseId.setValue(this.currentPhase.id);

        if(this.currentPhase.typ == GlobalEnumsAceTypphase.Bericht)
        {
          milestone.ErstelltInProjektBerichtId.setValue(this.currentReportId);
        }
      }

      dialogComponent.milestoneFormGroup.patchValue(milestone.value);
    }

    this.milestoneDialog.result.subscribe(result => {
      this.onMilestoneDialogClose(<MilestoneDialogResult>result);
      this.editedRowIndex = undefined;
    });
  }

  protected onMilestoneDialogClose(result: MilestoneDialogResult) {
    switch (result.action) {
      case ConfirmationDialogAction.Confirm:
        if (result.milestone) {
          if (this.editedRowIndex != undefined) {
            this.scheduleDataForm.Milestones.removeAt(this.editedRowIndex);
            this.scheduleDataForm.Milestones.insert(this.editedRowIndex, result.milestone);
          } else {
            this.scheduleDataForm.Milestones.push(result.milestone);
          }
          this.scheduleDataForm.markAsDirty();
          this.scheduleDataForm.markAsTouched();
          this.loadGridData();
          this.saveEvent.emit();
        }
        break;
      case ConfirmationDialogAction.Cancel:
      default:
        break;
    }
  }
  
  public editedFormIsInvalid() {
    return this.editedFormGroup?.invalid ?? true;
  }

  public milestonesEditable(model: MilestoneDisplayModel): boolean{
    return !this.scheduleDataForm?.disabled;
  }

  public milestonesDeleteable(model: MilestoneDisplayModel): boolean{
    return !this.scheduleDataForm?.disabled && model.erstelltInProjektBerichtId == this.currentReportId;
  }
}
